import { DG, EventStreamDefinition } from '@cotera/sdk/core';
import {
  BaseModel,
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { TargetableModel } from '@cotera/client/v0/actions/framework/types';
import { Constant, EraQL, Expression, Relation, Ty } from '@cotera/era';
import { JSONNode } from '@cotera/client/app/components/json-type-builder';
import { makeDefaultFilterGroup } from '@cotera/client/app/components/app/filter-builder/filter-builder';
import { Assert } from '@cotera/utilities';
import { FilterGroup } from '@cotera/client/app/components/app/filter-builder/types';
import { filterGroupToExpression } from '@cotera/client/app/components/app/filter-builder/utils';
import { Result } from 'neverthrow';
import { areArraysEqual } from '@cotera/client/app/etc';

export const STANDARD_SCHEDULES = ['hourly', 'daily', 'never'] as const;
export type StandardSchedule = (typeof STANDARD_SCHEDULES)[number];
export type Schedule = StandardSchedule | { cron: string };

type OutputShape = JSONNode;

const MAX_PREVIEW_ROWS = 300;

export class LlmColumnModel extends BaseModel implements TargetableModel {
  readonly currentNameInGraph: Watchable<string>;
  readonly dependencies: ReadonlyWatchable<string[]>;
  readonly formState: Watchable<{
    name: string;
    systemMessage: string;
    prompt: string;
    model: string;
    temperature: number;
    maxOutputTokens: number;
    outputShape: OutputShape;
    imageUrl: string | null;
    when: Schedule;
    tools: string[];
    durable: boolean;
  }>;
  readonly editorState: ReadonlyWatchable<DG.DocumentEditor.EditorState | null>;
  readonly v2EditorState: Watchable<DG.DocumentEditor.DocumentState>;
  readonly outputShape: ReadonlyWatchable<Ty.ExtendedAttributeType>;
  readonly whereEraQl: ReadonlyWatchable<string | null>;
  readonly filterGroup: Watchable<FilterGroup>;
  readonly config: ReadonlyWatchable<
    DG.Graph.LlmColConfig | DG.Graph.LlmColConfigV2 | null
  >;
  readonly datasetRel: ReadonlyWatchable<Result<
    DG.Graph.RelItem,
    DG.Graph.GraphError
  > | null>;
  readonly preview = Watchable.fromValue<Relation | null>(null);
  readonly changes: ReadonlyWatchable<Record<string, boolean | string>>;
  readonly hasChanges: ReadonlyWatchable<boolean>;
  readonly promptExpression: ReadonlyWatchable<EraQL.ParseRes<Expression>>;
  readonly promptEraql: ReadonlyWatchable<string>;
  readonly parser: ReadonlyWatchable<DG.Graph.RelativeParser>;
  readonly whereExpression: ReadonlyWatchable<Expression | null>;
  readonly route: ReadonlyWatchable<string>;
  readonly pageTitle: ReadonlyWatchable<string>;
  readonly errors: ReadonlyWatchable<{
    prompt: string | null;
    rel: string | null;
  }> = Watchable.fromValue({
    prompt: null,
    rel: null,
  });
  readonly hasErrors = Watchable.from((get) => {
    return Object.values(get(this.errors)).some((x) => x !== null);
  });
  readonly chatInput: Watchable<string> = Watchable.fromValue('');
  readonly chatMessages: Watchable<any[]> = Watchable.fromValue<any[]>([]);

  readonly eventStream: ReadonlyWatchable<EventStreamDefinition | null>;
  readonly hasBeenTargeted: Watchable<boolean> = Watchable.fromValue(false);

  readonly isNew: boolean;
  readonly datasetName: string;
  readonly identity: ReadonlyWatchable<{
    rel: DG.Graph.Identity | null;
    col: DG.Graph.Identity | null;
  }>;
  readonly version: ReadonlyWatchable<'v1' | 'v2'>;

  readonly runOnNewRowsClicked: Watchable<boolean> = Watchable.fromValue(false); // TODO future: can load a persisted value

  constructor(
    readonly inputs: {
      dataset: string;
      column: string;
      graph: ReadonlyWatchable<DG.BuildExplorer>;
    },
    opts: {
      targeted?: boolean;
      isNew: boolean;
    } = {
      targeted: false,
      isNew: false,
    }
  ) {
    super(`${inputs.dataset}-${inputs.column}`);

    this.isNew = opts.isNew;
    this.isTargeted = opts.targeted ?? false;
    this.datasetName = inputs.dataset;

    this.currentNameInGraph = Watchable.fromValue(inputs.column);

    this.datasetRel = Watchable.from((get) => {
      const graph = get(inputs.graph);

      return (
        graph
          .forSymbol(inputs.dataset)
          ?.builtNode.map((r) => {
            Assert.assert(r.item.t === 'rel');
            return r.item;
          })
          .mapErr((x) => x.err) ?? null
      );
    });

    this.config = Watchable.from((get) => {
      const res =
        get(inputs.graph).getColumn({
          rel: inputs.dataset,
          name: get(this.currentNameInGraph),
        })?.config ?? null;

      if (res === null) {
        return null;
      }

      Assert.assert(res.t === 'llm');

      return res;
    });

    this.version = Watchable.from((get) => {
      const config = get(this.config);

      if (opts.isNew) {
        return 'v2';
      }

      return config?.version ?? 'v1';
    });

    this.eventStream = Watchable.from((get) => {
      const col = get(inputs.graph).getColumn({
        rel: inputs.dataset,
        name: get(this.currentNameInGraph),
      });
      const es = col?.builtNode?.eventStream ?? null;
      return es;
    });

    // Initialize v2EditorState for editable editor state (v2 only)
    const initialV2State = (() => {
      const config = this.config.snapshot();
      if (config === null) {
        return { type: 'doc' as const, content: [] };
      }

      // For v2 configs, use the editorState from config
      if (config?.version === 'v2') {
        return config.editorState;
      }

      // For v1 configs, return empty document
      return { type: 'doc' as const, content: [] };
    })();
    this.v2EditorState = Watchable.fromValue(initialV2State);
    this.editorState = Watchable.from((get) => {
      return new DG.DocumentEditor.EditorState(get(this.v2EditorState));
    });

    this.dependencies = Watchable.from((get) => {
      return get(this.editorState)?.getDependencies(get(inputs.graph)) ?? [];
    });

    const meta = this.config.snapshot();

    // Helper to extract formState from config (handles both v1 and v2)
    const getFormStateFromConfig = (
      config: DG.Graph.LlmColConfig | DG.Graph.LlmColConfigV2 | null
    ) => {
      if (config === null) {
        return {
          name: this.isNew ? '' : this.currentNameInGraph.snapshot(),
          systemMessage: '',
          prompt: '',
          model: 'azure-gpt-5-nano',
          temperature: 0.5,
          maxOutputTokens: 4096,
          outputShape: DG.shorthandToResponseShape(Ty.ty('string')),
          when: 'never' as Schedule,
          imageUrl: null,
          tools: [],
          durable: true,
        };
      }

      // Handle v2 config
      if (config?.version === 'v2') {
        const editorState = new DG.DocumentEditor.EditorState(
          config.editorState
        );
        const graph = inputs.graph.snapshot();
        const messages = editorState.getMessages(graph, 'simple');
        const systemMessage =
          messages.find((m) => m.role === 'system')?.content ?? '';
        const userMessages = messages.filter((m) => m.role === 'user');
        const prompt = userMessages.map((m) => m.content).join('\n\n') || '';

        // Extract tools from mentions
        const mentions = editorState.getMentions(graph);
        const tools = mentions
          .filter((m) => m.type === 'tool')
          .map((m) => m.id);

        // Extract imageUrl from attachment nodes
        let imageUrl: string | null = null;
        const findAttachment = (
          nodes:
            | (DG.DocumentEditor.ContentNode | DG.DocumentEditor.TextNode)[]
            | undefined
        ): string | null => {
          if (!nodes) {
            return null;
          }
          for (const node of nodes) {
            if ('text' in node) {
              continue;
            }
            if (node.type === 'attachment') {
              return (node.attrs?.['value'] as string | undefined) ?? null;
            }
            if (node.content) {
              const found = findAttachment(node.content);
              if (found !== null) {
                return found;
              }
            }
          }
          return null;
        };
        imageUrl = findAttachment(config.editorState.content) ?? null;

        return {
          name: this.isNew
            ? ''
            : editorState.title() ?? this.currentNameInGraph.snapshot(),
          systemMessage,
          prompt,
          model: config.model,
          temperature: config.temperature ?? 0.5,
          maxOutputTokens: config.maxOutputTokens ?? 4096,
          outputShape: DG.shorthandToResponseShape(editorState.getOutputTy()),
          when: config.schedule ?? ('never' as Schedule),
          imageUrl,
          tools,
          durable: true, // v2 doesn't have durable field, default to true
        };
      }

      // Handle v1 config
      return {
        name: this.isNew ? '' : this.currentNameInGraph.snapshot(),
        systemMessage: config.systemMessage ?? '',
        prompt: cleanPrompt(config.prompt ?? ''),
        model: config.model ?? 'azure-gpt-5-nano',
        temperature: config.temperature ?? 0.5,
        maxOutputTokens: config.maxOutputTokens ?? 4096,
        outputShape: DG.shorthandToResponseShape(
          config.outputTy !== undefined
            ? Assert.assertOk(EraQL.parseTy(config.outputTy))
            : Ty.ty('string')
        ),
        when: config.when ?? ('never' as Schedule),
        imageUrl: config.imageUrl ?? null,
        tools: config.tools ?? [],
        durable: config.durable ?? true,
      };
    };

    const rel =
      this.datasetRel.snapshot()?.map((r) => {
        Assert.assert(r.t === 'rel');

        return r.rel;
      }) ?? null;

    this.formState = Watchable.fromValue(getFormStateFromConfig(meta), {
      updater: (x) => {
        return {
          ...x,
          mode: x.when === 'never' ? 'preview' : 'live',
        };
      },
    });

    const name = this.currentNameInGraph.snapshot();

    this.preview.set(
      rel?.isOk()
        ? rel.value
            .select((t) => {
              return {
                [name]: this.isNew
                  ? Constant(null, { ty: 'string' })
                  : t.attr(name),
                ...(this.isNew ? t.star() : t.except(name)),
              };
            })
            .limit(MAX_PREVIEW_ROWS)
        : null
    );

    this.outputShape = Watchable.from((get) => {
      const ty = get(this.formState).outputShape;

      return DG.responseShapeToShorthand(ty);
    });

    this.parser = Watchable.from((get) => {
      return get(this.inputs.graph).parser.forRel(this.datasetName);
    });

    this.filterGroup = Watchable.fromValue(makeDefaultFilterGroup());

    this.whereExpression = Watchable.from((get) => {
      const rel = get(this.datasetRel);
      if (!rel || rel.isErr()) {
        return null;
      }

      Assert.assert(rel.value.t === 'rel', 'Rel not found');
      const builtRel = rel.value.rel;

      return filterGroupToExpression(builtRel.ref('from'))(
        get(this.filterGroup)
      );
    });

    this.whereEraQl = Watchable.from((get) => {
      return get(this.whereExpression)?.toEraQL() ?? null;
    });

    this.promptEraql = Watchable.from((get) => {
      const prompt = get(this.formState).prompt;

      return `format_nullable\`${prompt}\``;
    });

    this.promptExpression = Watchable.from((get) => {
      return get(this.parser).parseExpr(get(this.promptEraql));
    });

    this.changes = Watchable.from((get) => {
      const initialEditorState = new DG.DocumentEditor.EditorState(
        (this.config.snapshot() as DG.Graph.LlmColConfigV2)?.editorState ??
          initialV2State
      );
      const config = get(this.config);
      const formState = get(this.formState);

      // For v2, compare against editorState
      if (config?.version === 'v2') {
        const currentEditorState = get(this.editorState);
        if (currentEditorState === null) {
          return {
            systemMessage: false,
            prompt: false,
            model: false,
            temperature: false,
            maxOutputTokens: false,
            outputShape: false,
            when: false,
            tools: false,
            durable: false,
          };
        }

        return {
          systemMessage: false,
          tools: false,
          durable: false,
          model:
            formState.model !== config.model
              ? `${config.model} -> ${formState.model}`
              : false,
          temperature:
            formState.temperature !== (config.temperature ?? 0.5)
              ? `${config.temperature ?? 0.5} -> ${formState.temperature}`
              : false,
          maxOutputTokens:
            formState.maxOutputTokens !== (config.maxOutputTokens ?? 4096)
              ? `${config.maxOutputTokens ?? 4096} -> ${
                  formState.maxOutputTokens
                }`
              : false,
          outputShape:
            Ty.displayTy(currentEditorState.getOutputTy()) !==
            Ty.displayTy(initialEditorState.getOutputTy())
              ? `${Ty.displayTy(
                  initialEditorState.getOutputTy()
                )} -> ${Ty.displayTy(currentEditorState.getOutputTy())}`
              : false,
          when: this.hasScheduleChanged()
            ? `${config.schedule ?? 'never'} -> ${formState.when}`
            : false,
          prompt:
            currentEditorState.toPromptMarkdown(get(inputs.graph)) !==
            initialEditorState.toPromptMarkdown(get(inputs.graph))
              ? true
              : false,
        };
      }

      // For v1, use existing logic
      const hasPrompChanged =
        cleanPrompt(get(this.promptEraql)) !==
        cleanPrompt((config as DG.Graph.LlmColConfig)?.prompt ?? '');

      const v1Config = config as DG.Graph.LlmColConfig | null;

      return {
        systemMessage:
          formState.systemMessage !== (v1Config?.systemMessage ?? ''),
        prompt: hasPrompChanged,
        model:
          formState.model !== (v1Config?.model ?? '')
            ? `${v1Config?.model ?? ''} -> ${formState.model}`
            : false,
        temperature:
          formState.temperature !== (v1Config?.temperature ?? 0.5)
            ? `${v1Config?.temperature ?? 0.5} -> ${formState.temperature}`
            : false,
        maxOutputTokens:
          formState.maxOutputTokens !== (v1Config?.maxOutputTokens ?? 4096)
            ? `${v1Config?.maxOutputTokens ?? 4096} -> ${
                formState.maxOutputTokens
              }`
            : false,
        outputShape:
          Ty.displayTy(get(this.outputShape)) !== (v1Config?.outputTy ?? '')
            ? `${v1Config?.outputTy ?? 'None'} -> ${Ty.displayTy(
                get(this.outputShape)
              )}`
            : false,
        when: this.hasScheduleChanged()
          ? `${v1Config?.when ?? 'never'} -> ${formState.when}`
          : false,
        tools: !areArraysEqual(formState.tools, v1Config?.tools ?? []),
        durable: formState.durable !== (v1Config?.durable ?? true),
      };
    });

    this.hasChanges = Watchable.from((get) => {
      return Object.values(get(this.changes)).some((x) => x !== false);
    });

    this.route = Watchable.from((get) => {
      const currentName = get(this.formState).name;

      if (this.isNew) {
        return `/dataset/${encodeURIComponent(
          this.inputs.dataset
        )}/column/new/llm`;
      }

      return `/dataset/${encodeURIComponent(
        this.inputs.dataset
      )}/column/${currentName}`;
    });

    this.pageTitle = Watchable.from((get) => {
      if (this.isNew) {
        return `${this.inputs.dataset}/New LLM Column`;
      }

      return `${this.inputs.dataset}/${get(this.formState).name}`;
    });

    this.errors = Watchable.from((get) => {
      return {
        prompt: get(this.promptExpression).isErr() ? 'Invalid prompt' : null,
        rel: get(this.datasetRel)?.isErr() ? 'Invalid rel' : null,
      };
    });

    this.identity = Watchable.from((get) => {
      return {
        rel:
          get(inputs.graph).getRel({ symbol: inputs.dataset })?.rawNode.meta
            .identity ?? null,
        col:
          get(inputs.graph).getColumn({
            rel: inputs.dataset,
            name: get(this.currentNameInGraph),
          })?.meta.identity ?? null,
      };
    });
  }

  isTargeted: boolean;

  target(): void {
    this.isTargeted = true;
    this.hasBeenTargeted.set(true);
  }

  markAsNotTargeted(): void {
    this.isTargeted = false;
  }

  hasScheduleChanged(): boolean {
    const config = this.config.snapshot();
    if (config === null) {
      return false;
    }

    const current =
      'version' in config && config.version === 'v2'
        ? config.schedule ?? 'never'
        : (config as DG.Graph.LlmColConfig).when ?? 'never';
    const newWhen = this.formState.snapshot()?.when;

    return JSON.stringify(current) !== JSON.stringify(newWhen);
  }

  isEqualTo(other: LlmColumnModel) {
    return this.id === other.id;
  }
}

/**
 * Cleans the prompt by removing the format_nullable` prefix and backticks.
 * used because the stored prompt is stored with the format_nullable` prefix and backticks
 * but we dont want to show it to the user
 * @param prompt - The prompt to clean.
 * @returns The cleaned prompt.
 */
const cleanPrompt = (prompt: string) => {
  if (!prompt || prompt.length === 0) {
    return '';
  }

  if (prompt.startsWith('format_nullable`') || prompt.startsWith('`')) {
    return prompt.replace('format_nullable`', '`').slice(1, -1); // remove backticks
  }

  return prompt;
};
